import React, { useState, useMemo, useRef, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  ShoppingCart,
  FileText,
  Plus,
  Minus,
  Trash2,
  Search,
  Package,
  Save,
  X,
  Loader2,
  CheckCircle2,
  ShoppingBag,
  Layers,
  ChevronDown,
  Filter
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { generateDocNumber } from '@/lib/stockApi';
import { upsertCustomerPricesFromItems, buildPriceHistory, lastPurchase } from '@/lib/customerPriceApi';
import { buildVariantOptions, groupVariantsByProduct } from '@/lib/productVariantOptions';
import { fetchPdfSettings } from '@/lib/pdfRenderer';
import { leseMindermengeRegel, warenwertNetto, berechneZuschlag, mitZuschlag, summen } from '@/lib/mindermenge';

/**
 * Order Form Component – Redesigned
 * Mobile-first sipariş formu with product cards, variant chips, category filters
 *
 * @param {Object} lead - Müşteri bilgileri
 * @param {string} orderType - 'Bestellung' veya 'Angebot'
 * @param {function} onSubmit - Form gönderildiğinde
 * @param {function} onCancel - İptal edildiğinde
 */
export default function OrderForm({ lead, orderType = 'Bestellung', onSubmit, onCancel, initialItems = [] }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [orderItems, setOrderItems] = useState(initialItems);
  const [priceInputs, setPriceInputs] = useState(
    () => Object.fromEntries(initialItems.map((it, i) => [i, String(it.unit_price ?? '')]))
  );
  // Rabatt (%) girişleri — pozisyon bazında indirim (özellikle Angebot)
  const [rabattInputs, setRabattInputs] = useState(
    () => Object.fromEntries(initialItems.map((it, i) => [i, String(it.rabatt_prozent ?? 0)]))
  );
  // Erhöhung (%) girişleri — pozisyon bazında fiyat artışı (Bestellung)
  const [erhoehungInputs, setErhoehungInputs] = useState(
    () => Object.fromEntries(initialItems.map((it, i) => [i, String(it.erhoehung_prozent ?? 0)]))
  );
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const cartRef = useRef(null);

  // Sepet açıldığında Zusammenfassung'a kaydır
  useEffect(() => {
    if (showCart) {
      cartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showCart]);

  // Ürünleri getir
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: async () => (await base44.entities.Product.list()).filter((p) => p.is_active !== false && p.aktiv !== false),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Varianten aus product_variants (Shopify-Sync/Editor). Fallback auf die
  // alte JSON-Spalte übernimmt buildVariantOptions.
  const { data: variantRows = [] } = useQuery({
    queryKey: ['product-variants'],
    queryFn: () => base44.entities.ProductVariant.list('position'),
    refetchInterval: 60_000,
  });
  const variantsByProduct = useMemo(() => groupVariantsByProduct(variantRows), [variantRows]);
  const getVariants = (product) => buildVariantOptions(product, variantsByProduct.get(product.id));

  // Müşteriye özel fiyatları getir
  const { data: customerPrices = [] } = useQuery({
    queryKey: ['customer-prices', lead?.id],
    queryFn: () => base44.entities.CustomerPrice.filter({ lead_id: lead?.id }),
    enabled: !!lead?.id,
  });

  // Müşteriye özel fiyat haritası
  const customerPriceMap = useMemo(() => {
    const map = {};
    customerPrices.forEach(cp => {
      map[cp.product_id] = cp.custom_price;
    });
    return map;
  }, [customerPrices]);

  const getProductPrice = (product) => {
    return customerPriceMap[product.id] ?? product.price ?? 0;
  };

  // Müşterinin geçmiş siparişleri → ürün bazlı tarih damgalı fiyat
  // geçmişi. Zam sonrası "eskiden kaça veriyorduk?" satır üstünde görünür.
  const { data: leadOrders = [] } = useQuery({
    queryKey: ['lead-orders', lead?.id],
    queryFn: () => base44.entities.Order.filter({ lead_id: lead?.id }),
    enabled: !!lead?.id,
  });
  const priceHistoryMap = useMemo(() => buildPriceHistory(leadOrders), [leadOrders]);

  // Kategorileri dynamisch aus Produkten generieren
  const categories = useMemo(() => {
    const cats = new Set();
    products.forEach(p => {
      if (p.category) cats.add(p.category);
    });
    return ['all', ...Array.from(cats).sort()];
  }, [products]);

  // Filtrelenmiş ürünler
  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(term) ||
        p.sku?.toLowerCase().includes(term) ||
        (variantsByProduct.get(p.id) || []).some((v) => v.sku?.toLowerCase().includes(term)) ||
        p.category?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term)
      );
    }

    // Daha önce sipariş verilen ürünler EN ÜSTTE (son alım tarihine göre),
    // ardından özel fiyatlılar — agent müşterinin ürünlerini aramadan bulur.
    const rank = (p) => {
      const last = lastPurchase(priceHistoryMap, p.id);
      if (last) return new Date(last.date).getTime();
      if (customerPriceMap[p.id] != null) return 1;
      return 0;
    };
    return [...filtered].sort((a, b) => rank(b) - rank(a));
  }, [products, searchTerm, selectedCategory, priceHistoryMap, customerPriceMap, variantsByProduct]);

  // Ürün/varyant ekle
  const addItem = (product, variant = null) => {
    const itemKey = variant
      ? `${product.id}_${variant.key}`
      : `${product.id}`;

    const existingIndex = orderItems.findIndex(item => item._key === itemKey);

    if (existingIndex >= 0) {
      const newItems = [...orderItems];
      newItems[existingIndex].quantity += 1;
      setOrderItems(newItems);
    } else {
      // Kundenpreis > Varianten-B2B-Preis > Produktpreis
      const price = customerPriceMap[product.id] ?? variant?.price ?? getProductPrice(product);
      const newIndex = orderItems.length;
      const itemName = variant
        ? `${product.name} — ${variant.value}`
        : product.name;

      setOrderItems([
        ...orderItems,
        {
          _key: itemKey,
          product_id: product.id,
          product_name: itemName,
          product_sku: variant?.sku || product.sku,
          product_image: variant?.image || product.image_url,
          description: product.description || '',
          // Stok motoru (reserve_stock/commit_stock RPC) `variant_value ?? variant`
          // okur ve yalnız eski JSON varyantlarını tanır. Tablo varyantında
          // stockValue null → stok ürün seviyesinde (variant_not_found olmaz).
          variant: variant?.stockValue || null,
          variant_value: variant?.stockValue || null,
          variant_label: variant?.value || null,
          variant_sku: variant?.sku || null,
          quantity: 1,
          unit_price: price,
          rabatt_prozent: 0,
          erhoehung_prozent: 0,
          unit: product.unit || 'Stück',
        }
      ]);
      setPriceInputs(prev => ({ ...prev, [newIndex]: String(price) }));
      setRabattInputs(prev => ({ ...prev, [newIndex]: '0' }));
      setErhoehungInputs(prev => ({ ...prev, [newIndex]: '0' }));
    }

    // Collapse product after adding
    setExpandedProduct(null);
  };

  // Miktar güncelle
  const updateQuantity = (index, delta) => {
    const newItems = [...orderItems];
    newItems[index].quantity = Math.max(1, newItems[index].quantity + delta);
    setOrderItems(newItems);
  };

  // Ürün kaldır
  const removeItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
    // Re-index price + rabatt + erhöhung inputs
    const newPriceInputs = {};
    const newRabattInputs = {};
    const newErhoehungInputs = {};
    orderItems.filter((_, i) => i !== index).forEach((item, newIdx) => {
      const oldIdx = newIdx >= index ? newIdx + 1 : newIdx;
      newPriceInputs[newIdx] = priceInputs[oldIdx] ?? String(item.unit_price);
      newRabattInputs[newIdx] = rabattInputs[oldIdx] ?? String(item.rabatt_prozent ?? 0);
      newErhoehungInputs[newIdx] = erhoehungInputs[oldIdx] ?? String(item.erhoehung_prozent ?? 0);
    });
    setPriceInputs(newPriceInputs);
    setRabattInputs(newRabattInputs);
    setErhoehungInputs(newErhoehungInputs);
  };

  // Fiyat güncelle — negatif fiyatları reddeder
  const updatePrice = (index, rawValue) => {
    setPriceInputs(prev => ({ ...prev, [index]: rawValue }));
    const parsed = parseFloat(rawValue);
    if (!isNaN(parsed) && parsed >= 0) {
      const newItems = [...orderItems];
      newItems[index] = { ...newItems[index], unit_price: parsed };
      setOrderItems(newItems);
    }
  };

  const handlePriceBlur = (index) => {
    const stored = orderItems[index]?.unit_price ?? 0;
    const raw = priceInputs[index] ?? '';
    if (raw === '' || isNaN(parseFloat(raw))) {
      setPriceInputs(prev => ({ ...prev, [index]: String(stored) }));
    }
  };

  // Rabatt (%) güncelle — 0..100 aralığına sabitlenir
  const updateRabatt = (index, rawValue) => {
    setRabattInputs(prev => ({ ...prev, [index]: rawValue }));
    const parsed = parseFloat(rawValue);
    if (!isNaN(parsed)) {
      const clamped = Math.min(100, Math.max(0, parsed));
      const newItems = [...orderItems];
      newItems[index] = { ...newItems[index], rabatt_prozent: clamped };
      setOrderItems(newItems);
    }
  };

  const handleRabattBlur = (index) => {
    const stored = orderItems[index]?.rabatt_prozent ?? 0;
    const raw = rabattInputs[index] ?? '';
    if (raw === '' || isNaN(parseFloat(raw)) || parseFloat(raw) !== stored) {
      setRabattInputs(prev => ({ ...prev, [index]: String(stored) }));
    }
  };

  // Erhöhung (%) güncelle — 0..500 aralığına sabitlenir
  const updateErhoehung = (index, rawValue) => {
    setErhoehungInputs(prev => ({ ...prev, [index]: rawValue }));
    const parsed = parseFloat(rawValue);
    if (!isNaN(parsed)) {
      const clamped = Math.min(500, Math.max(0, parsed));
      const newItems = [...orderItems];
      newItems[index] = { ...newItems[index], erhoehung_prozent: clamped };
      setOrderItems(newItems);
    }
  };

  const handleErhoehungBlur = (index) => {
    const stored = orderItems[index]?.erhoehung_prozent ?? 0;
    const raw = erhoehungInputs[index] ?? '';
    if (raw === '' || isNaN(parseFloat(raw)) || parseFloat(raw) !== stored) {
      setErhoehungInputs(prev => ({ ...prev, [index]: String(stored) }));
    }
  };

  // Fiyat zinciri: Einzelpreis → +Erhöhung → −Rabatt → × Menge
  const increasedUnitPrice = (item) =>
    item.unit_price * (1 + (Number(item.erhoehung_prozent) || 0) / 100);

  const discountedUnitPrice = (item) =>
    increasedUnitPrice(item) * (1 - (Number(item.rabatt_prozent) || 0) / 100);

  const lineTotal = (item) => item.quantity * discountedUnitPrice(item);

  // Mindestbestellwert / Mindermengenzuschlag (Admin → PDF-Einstellungen → Bestellung).
  // Nur für Bestellungen; Warenwert = Positionen nach Zeilen- und Kundenrabatt.
  const regelQ = useQuery({ queryKey: ['pdf-settings-regel'], queryFn: fetchPdfSettings, staleTime: 5 * 60 * 1000 });
  const mindermengeRegel = useMemo(() => leseMindermengeRegel(regelQ.data), [regelQ.data]);
  const kundenrabattProzent = Number(lead?.kundenrabatt_prozent) || 0;
  const warenwert = warenwertNetto(
    orderItems.map((it) => ({ ...it, unit_price: increasedUnitPrice(it), discounted_unit_price: undefined })),
    { kundenrabattProzent }
  );
  const zuschlagInfo = orderType === 'Angebot'
    ? { faellig: false, betrag: 0, fehlbetrag: 0 }
    : berechneZuschlag(warenwert, mindermengeRegel);
  const liveSums = summen([
    ...orderItems.map((it) => ({ total: lineTotal(it) })),
    ...(zuschlagInfo.faellig ? [{ total: zuschlagInfo.betrag }] : []),
  ]);

  /**
   * Sipariş/teklif numarası: KM/LS/RE ile aynı atomik DB sequence'ından
   * (AN-2026-0001 / BS-2026-0001) — çakışma riski yok. RPC ulaşılamazsa
   * eski rastgele biçime düşülür ki sipariş oluşturma asla bloklanmasın.
   */
  const generateOrderNumber = async () => {
    const docType = orderType === 'Angebot' ? 'AN' : 'BS';
    try {
      const num = await generateDocNumber(docType);
      if (num) return num;
    } catch { /* fallback aşağıda */ }
    const prefix = orderType === 'Angebot' ? 'ANG' : 'BST';
    const date = new Date();
    const dateStr = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
    const random = Math.random().toString(36).substr(2, 4).toUpperCase();
    return `${prefix}-${dateStr}-${random}`;
  };

  const handleSubmit = async () => {
    if (orderItems.length === 0) return;

    // Yazma öncesi doğrulama — negatif veya geçersiz değerleri reddet
    const invalid = orderItems.find((item) => {
      const q = Number(item.quantity);
      const p = Number(item.unit_price);
      const r = Number(item.rabatt_prozent ?? 0);
      const e = Number(item.erhoehung_prozent ?? 0);
      return !Number.isFinite(q) || q <= 0 || !Number.isFinite(p) || p < 0
        || !Number.isFinite(r) || r < 0 || r > 100
        || !Number.isFinite(e) || e < 0;
    });
    if (invalid) {
      toast.error('Bitte gültige Menge (> 0), Preis (≥ 0), Erhöhung (≥ 0 %) und Rabatt (0–100 %) für alle Positionen angeben.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Clean _key before submitting; erhöhung/rabatt sonuçlarını kalemlere yaz
      const cleanItems = orderItems.map(({ _key, ...rest }) => ({
        ...rest,
        erhoehung_prozent: Number(rest.erhoehung_prozent) || 0,
        rabatt_prozent: Number(rest.rabatt_prozent) || 0,
        // Erhöhung uygulanmış birim fiyat — sipariş sonrası müşteri fiyatı
        // olarak kaydedilir; kalem hesabı da bunun üzerinden yapılır.
        unit_price: Math.round(increasedUnitPrice(rest) * 100) / 100,
        base_unit_price: rest.unit_price,
        discounted_unit_price: Math.round(discountedUnitPrice(rest) * 100) / 100,
        total: Math.round(lineTotal(rest) * 100) / 100,
      }));

      // Bestellung unter Mindestbestellwert → Zuschlagszeile (idempotent); Angebot ohne.
      const finalItems = orderType === 'Angebot'
        ? cleanItems
        : mitZuschlag(cleanItems, mindermengeRegel, { kundenrabattProzent });
      const sums = summen(finalItems);

      const orderData = {
        order_number: await generateOrderNumber(),
        order_type: orderType,
        lead_id: lead.id,
        lead_name: lead.name,
        lead_address: [lead.strasse, lead.plz, lead.ort].filter(Boolean).join(', '),
        lead_email: lead.email,
        lead_phone: lead.telefon,
        items: finalItems,
        // Stok motoru commit_stock RPC'si orders.positionen kolonundan
        // okur. Eski kod items'tan okur. Geçiş döneminde ikisini aynı
        // veri ile dolduruyoruz; ileride bir tanesini emekli edebiliriz.
        positionen: finalItems,
        subtotal: sums.subtotal,
        tax_rate: 19,
        tax_amount: sums.tax_amount,
        total: sums.total,
        notes,
        status: 'Neu',
        created_date: new Date().toISOString(),
      };

      await onSubmit(orderData);

      // Bestellung tamamlandı → Erhöhung girilen ürünlerde yeni (artırılmış)
      // Einzelpreis müşteriye özel fiyat olarak kaydedilir ve eskisinin
      // yerini alır. Erhöhung girilmeyen ürünlerin fiyatı değişmez.
      if (orderType !== 'Angebot') {
        const increased = cleanItems.filter((it) => it.erhoehung_prozent > 0 && it.product_id);
        if (increased.length > 0) {
          const { saved, errors } = await upsertCustomerPricesFromItems({
            leadId: lead.id,
            leadName: lead.name || '',
            items: increased,
          });
          errors.forEach(({ productName, message }) =>
            toast.warning(`Preis für "${productName}" konnte nicht gespeichert werden: ${message}`)
          );
          if (saved > 0) {
            toast.success(`${saved} Kundenpreis(e) mit erhöhtem Einzelpreis aktualisiert.`);
          }
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isOffer = orderType === 'Angebot';
  const Icon = isOffer ? FileText : ShoppingCart;
  const accentColor = isOffer ? 'orange' : 'emerald';

  // Check if item is in cart
  const getCartItem = (product, variant = null) => {
    const itemKey = variant ? `${product.id}_${variant.key}` : `${product.id}`;
    return orderItems.find(item => item._key === itemKey);
  };

  return (
    <div className="space-y-4 pb-4">
      {/* Header */}
      <div className={cn(
        'px-4 py-3 -mx-4 -mt-2 rounded-b-xl',
        isOffer ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-gradient-to-r from-emerald-600 to-teal-500'
      )}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="text-white min-w-0 flex-1">
            <h2 className="font-bold text-lg">{isOffer ? 'Neues Angebot' : 'Neue Bestellung'}</h2>
            <p className="text-sm text-white/80 truncate">{lead?.name}</p>
          </div>

          {/* Sepet ikonu + adet rozeti → tıklayınca Zusammenfassung */}
          <button
            onClick={() => setShowCart((prev) => !prev)}
            disabled={orderItems.length === 0}
            aria-label={`Warenkorb anzeigen (${orderItems.length} Positionen)`}
            aria-expanded={showCart}
            className={cn(
              'relative w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all',
              orderItems.length > 0
                ? 'bg-white/25 backdrop-blur text-white hover:bg-white/35 active:scale-95'
                : 'bg-white/10 text-white/40 cursor-default'
            )}
          >
            <ShoppingBag className="w-5 h-5" />
            {orderItems.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-white text-xs font-bold flex items-center justify-center shadow-sm text-slate-800">
                {orderItems.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Produkt suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9 h-11 text-base"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
      </div>

      {/* Category Filter */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={cn(
              'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all',
              selectedCategory === cat
                ? (isOffer ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-300' : 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300')
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            )}
          >
            {cat === 'all' ? '🏷️ Alle' : cat}
          </button>
        ))}
      </div>

      {/* Product List */}
      {productsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-2">
          {filteredProducts.length === 0 && (
            <div className="text-center py-8">
              <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-400">Keine Produkte gefunden</p>
            </div>
          )}

          {filteredProducts.slice(0, 30).map(product => {
            const variants = getVariants(product);
            const hasVariants = variants.length > 0;
            const isExpanded = expandedProduct === product.id;
            const cartItem = !hasVariants ? getCartItem(product) : null;
            const inCart = !!cartItem;

            // Count total variants in cart for this product
            const variantsInCart = hasVariants
              ? variants.filter(v => getCartItem(product, v)).length
              : 0;

            return (
              <div key={product.id} className="rounded-xl border bg-white overflow-hidden transition-shadow hover:shadow-sm">
                {/* Product Row */}
                <button
                  onClick={() => {
                    if (hasVariants) {
                      setExpandedProduct(isExpanded ? null : product.id);
                    } else {
                      addItem(product);
                    }
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 text-left transition-colors',
                    inCart && 'bg-emerald-50/50'
                  )}
                >
                  {/* Image */}
                  <div className="relative w-14 h-14 rounded-lg bg-slate-100 flex-shrink-0 overflow-hidden border">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <Package className="w-6 h-6 text-slate-300 m-auto mt-4" />
                    )}
                    {inCart && (
                      <div className="absolute inset-0 bg-emerald-600/80 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{cartItem.quantity}×</span>
                      </div>
                    )}
                    {variantsInCart > 0 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-[10px] font-bold">{variantsInCart}</span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className={cn('font-semibold text-sm', inCart && 'text-emerald-800')}>
                      {product.name}
                    </p>
                    {product.description && (
                      <p className="text-xs text-slate-400 truncate mt-0.5">
                        {product.description.substring(0, 60)}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {/* Tarih damgalı son satış fiyatı — zam sonrası referans */}
                      {(() => {
                        const last = lastPurchase(priceHistoryMap, product.id);
                        if (!last) return null;
                        return (
                          <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200 font-semibold">
                            Zuletzt €{last.price.toFixed(2)} · {new Date(last.date).toLocaleDateString('de-DE')}
                          </span>
                        );
                      })()}
                      {product.category && (
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                          {product.category}
                        </span>
                      )}
                      {hasVariants && (
                        <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <Layers className="w-2.5 h-2.5" />
                          {variants.length} Varianten
                        </span>
                      )}
                      {/* Stok görünürlüğü: available = stock - reserved.
                          Sayım henüz tamamlanmamışsa "Stand unbekannt" — agent
                          yine sipariş verebilir ama stok doğrulanmadı. */}
                      {product.is_counted === false ? (
                        <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">
                          Stand unbekannt
                        </span>
                      ) : !hasVariants ? (
                        (() => {
                          const avail = (product.available_stock ?? (product.stock ?? 0) - (product.reserved_stock ?? 0));
                          if (avail <= 0) {
                            return (
                              <span className="text-[10px] bg-red-50 text-red-700 px-1.5 py-0.5 rounded border border-red-200">
                                Nicht verfügbar
                              </span>
                            );
                          }
                          if (avail < 5) {
                            return (
                              <span className="text-[10px] bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded border border-orange-200">
                                Nur {avail} verfügbar
                              </span>
                            );
                          }
                          return (
                            <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
                              {avail} verfügbar
                            </span>
                          );
                        })()
                      ) : null}
                    </div>
                  </div>

                  {/* Action hint */}
                  <div className="flex-shrink-0">
                    {hasVariants ? (
                      <ChevronDown className={cn(
                        'w-5 h-5 text-slate-400 transition-transform',
                        isExpanded && 'rotate-180 text-blue-500'
                      )} />
                    ) : inCart ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <Plus className={cn(
                        'w-5 h-5',
                        isOffer ? 'text-orange-400' : 'text-emerald-400'
                      )} />
                    )}
                  </div>
                </button>

                {/* Variant Chips (expanded) */}
                {hasVariants && isExpanded && (
                  <div className="px-3 pb-3 border-t bg-slate-50/50">
                    <p className="text-xs text-slate-500 font-medium py-2">Variante auswählen:</p>
                    <div className="flex flex-wrap gap-2">
                      {variants.map((variant) => {
                        const vCartItem = getCartItem(product, variant);
                        const vInCart = !!vCartItem;

                        return (
                          <button
                            key={variant.key}
                            onClick={() => addItem(product, variant)}
                            className={cn(
                              'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all',
                              vInCart
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 ring-1 ring-emerald-200'
                                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                            )}
                          >
                            {variant.image && (
                              <img loading="lazy" decoding="async" src={variant.image} alt="" className="w-6 h-6 rounded object-cover border" />
                            )}
                            <span className="font-medium">{variant.value}</span>
                            {variant.sku && (
                              <span className="text-[10px] text-slate-400">{variant.sku}</span>
                            )}
                            {vInCart && (
                              <span className="text-[10px] bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded-full font-bold">
                                {vCartItem.quantity}×
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {filteredProducts.length > 30 && (
            <p className="text-xs text-slate-400 text-center py-2">
              {filteredProducts.length - 30} weitere Produkte — Suche verwenden zum filtern
            </p>
          )}
        </div>
      )}

      {/* Warenkorb / Zusammenfassung — sepet ikonuna tıklayınca açılır */}
      {orderItems.length > 0 && showCart && (
        <Card ref={cartRef} className="border-t-2 scroll-mt-4" style={{ borderTopColor: isOffer ? '#f97316' : '#10b981' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <ShoppingBag className="w-4 h-4" />
                Zusammenfassung ({orderItems.length})
              </span>
              <button
                onClick={() => setShowCart(false)}
                aria-label="Zusammenfassung schließen"
                className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {orderItems.map((item, index) => (
              <div
                key={item._key || index}
                className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-2.5"
              >
                {/* Üst satır: görsel + isim/varyant + sil */}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-white flex-shrink-0 overflow-hidden border">
                    {item.product_image ? (
                      <img loading="lazy" decoding="async" src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-5 h-5 text-slate-300 m-auto mt-3.5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-800 leading-tight break-words">{item.product_name}</p>
                    {(item.variant_label || item.variant) && (
                      <span className="inline-block text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded mt-0.5">
                        {item.variant_label || item.variant}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeItem(index)}
                    aria-label="Position entfernen"
                    className="p-2 -m-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Orta satır: adet + satır toplamı */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-0.5 bg-white rounded-lg border">
                    <button
                      onClick={() => updateQuantity(index, -1)}
                      aria-label="Menge verringern"
                      className="w-10 h-10 flex items-center justify-center text-slate-500 hover:bg-slate-50 active:bg-slate-100 rounded-l-lg"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-10 text-center text-base font-semibold">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(index, 1)}
                      aria-label="Menge erhöhen"
                      className="w-10 h-10 flex items-center justify-center text-slate-500 hover:bg-slate-50 active:bg-slate-100 rounded-r-lg"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="text-base font-bold text-slate-700 text-right">
                    {(Number(item.rabatt_prozent) || 0) > 0 && (
                      <span className="block text-[10px] font-normal text-slate-400 line-through">
                        €{(item.quantity * item.unit_price).toFixed(2)}
                      </span>
                    )}
                    €{lineTotal(item).toFixed(2)}
                  </span>
                </div>

                {/* Alt satır: fiyat alanları — etiketli grid, yatay kaydırma yok */}
                <div className={cn('grid gap-2', isOffer ? 'grid-cols-2' : 'grid-cols-3')}>
                  <label className="block min-w-0">
                    <span className="block text-[10px] font-medium text-slate-400 mb-0.5">Einzelpreis</span>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">€</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={priceInputs[index] ?? item.unit_price}
                        onChange={(e) => updatePrice(index, e.target.value)}
                        onBlur={() => handlePriceBlur(index)}
                        onFocus={(e) => e.target.select()}
                        className="h-10 w-full text-base text-right pl-6 pr-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      />
                    </div>
                  </label>

                  {!isOffer && (
                    <label className="block min-w-0">
                      <span className="block text-[10px] font-medium text-slate-400 mb-0.5">Erhöhung %</span>
                      <div className="relative">
                        <input
                          type="number"
                          inputMode="decimal"
                          step="1"
                          min="0"
                          value={erhoehungInputs[index] ?? item.erhoehung_prozent ?? 0}
                          onChange={(e) => updateErhoehung(index, e.target.value)}
                          onBlur={() => handleErhoehungBlur(index)}
                          onFocus={(e) => e.target.select()}
                          className={cn(
                            'h-10 w-full text-base text-right pl-2 pr-7 border rounded-lg bg-white focus:outline-none focus:ring-2',
                            (Number(item.erhoehung_prozent) || 0) > 0
                              ? 'border-blue-300 text-blue-700 font-semibold focus:ring-blue-400'
                              : 'border-slate-200 focus:ring-blue-400'
                          )}
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
                      </div>
                    </label>
                  )}

                  <label className="block min-w-0">
                    <span className="block text-[10px] font-medium text-slate-400 mb-0.5">Rabatt %</span>
                    <div className="relative">
                      <input
                        type="number"
                        inputMode="decimal"
                        step="1"
                        min="0"
                        max="100"
                        value={rabattInputs[index] ?? item.rabatt_prozent ?? 0}
                        onChange={(e) => updateRabatt(index, e.target.value)}
                        onBlur={() => handleRabattBlur(index)}
                        onFocus={(e) => e.target.select()}
                        className={cn(
                          'h-10 w-full text-base text-right pl-2 pr-7 border rounded-lg bg-white focus:outline-none focus:ring-2',
                          (Number(item.rabatt_prozent) || 0) > 0
                            ? 'border-orange-300 text-orange-700 font-semibold focus:ring-orange-400'
                            : 'border-slate-200 focus:ring-orange-400'
                        )}
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
                    </div>
                  </label>
                </div>
              </div>
            ))}

            {/* Totals */}
            <div className="border-t pt-3 mt-3 space-y-1.5">
              {zuschlagInfo.faellig && (
                <div role="status" className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <span className="font-semibold">Mindermengenzuschlag {zuschlagInfo.betrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € netto</span>
                  {' '}— Warenwert {warenwert.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € liegt unter dem Mindestbestellwert
                  {' '}{mindermengeRegel.mindestbestellwert.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € netto (es fehlen {zuschlagInfo.fehlbetrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €).
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Zwischensumme</span>
                <span className="font-medium">€{liveSums.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">MwSt. (19%)</span>
                <span>€{liveSums.tax_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>Gesamt</span>
                <span className={isOffer ? 'text-orange-600' : 'text-emerald-600'}>
                  €{liveSums.total.toFixed(2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      <Card>
        <CardContent className="pt-4">
          <Label htmlFor="notes">Notizen</Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Zusätzliche Bemerkungen..."
            rows={2}
            className="mt-2"
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 h-12"
        >
          <X className="w-4 h-4 mr-2" />
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={orderItems.length === 0 || isSubmitting}
          className={cn(
            'flex-1 h-12 font-semibold',
            isOffer ? 'bg-orange-500 hover:bg-orange-600' : 'bg-emerald-500 hover:bg-emerald-600'
          )}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Wird erstellt...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isOffer ? 'Angebot erstellen' : 'Bestellung aufgeben'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
